import { PhantomWalletAdapter } from "@solana/wallet-adapter-phantom";
import { ConnectionProvider, WalletProvider } from "@solana/wallet-adapter-react";
import { WalletModalProvider } from "@solana/wallet-adapter-react-ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Analytics as VercelAnalytics } from "@vercel/analytics/react";
import { useMemo } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { getCurrentRpcEndpoint } from "@/config";
import { MotionLazy } from "./components/animate/motion-lazy";
import InviteDialog from "./components/invite/InviteDialog";
import { RouteLoadingProgress } from "./components/loading";
import Toast from "./components/toast";
import { GLOBAL_CONFIG } from "./global-config";
import { AntdAdapter } from "./theme/adapter/antd.adapter";
import { ThemeProvider } from "./theme/theme-provider";

// Default styles that can be overridden by your app
import "@solana/wallet-adapter-react-ui/styles.css";

if (import.meta.env.DEV) {
	import("react-scan").then(({ scan }) => {
		scan({
			enabled: false,
			showToolbar: true,
			log: false,
			animationSpeed: "fast",
		});
	});
}

function App({ children }: { children: React.ReactNode }) {
	// Solana wallet configuration
	const endpoint = useMemo(() => getCurrentRpcEndpoint(), []);
	const wallets = useMemo(() => [new PhantomWalletAdapter()], []);

	return (
		<ConnectionProvider endpoint={endpoint}>
			<WalletProvider wallets={wallets} autoConnect>
				<WalletModalProvider>
					<HelmetProvider>
						<QueryClientProvider client={new QueryClient()}>
							<ThemeProvider adapters={[AntdAdapter]}>
								<VercelAnalytics debug={import.meta.env.PROD} />
								<Helmet>
									<title>{GLOBAL_CONFIG.appName}</title>
								</Helmet>
							<Toast />
							<InviteDialog />
							<RouteLoadingProgress />
							<MotionLazy>{children}</MotionLazy>
							</ThemeProvider>
						</QueryClientProvider>
					</HelmetProvider>
				</WalletModalProvider>
			</WalletProvider>
		</ConnectionProvider>
	);
}

export default App;
